import React, { useState, useEffect } from 'react';
import { useSwipeBack } from '../use-swipe-back.js';
import { F as _F, haptic, StatusIcon } from '../theme.jsx';
import {
  modalShellStyle, bottomSheetShellStyle, ModalHeader, ModalCloseButton, BottomSheetHeader,
} from '../modal-utils.jsx';
import { useToast } from '../components/boothly/toast-provider.jsx';
import { Z } from '../lib/z-index.js';
import { fmtCurrency } from '../lib/format.js';

// v2-Feld-Optik (kontrastfest, gleich wie lager-/product-Sheets): klar gegen die
// Sheet-Surface abgesetzt (--fill-secondary) + Hairline-Border, Wert-Text --label-primary.
const inputCls =
  'w-full rounded-xl border border-[var(--border-default)] bg-[var(--fill-secondary)] px-4 py-3 text-sm text-[var(--label-primary)] outline-none placeholder:text-muted-foreground focus:ring-2 focus:ring-ios-blue/50';

// ── Utilities (keep in sync with online-store.jsx) ────────────
const today = () => new Date().toISOString().slice(0, 10);
const STATUS = {
  Offen:     { color: "rgba(55,138,221,0.85)",  text: "#fff" },
  Bereit:    { color: "rgba(255,159,10,0.85)",  text: "#fff" },
  Versendet: { color: "rgba(48,209,88,0.85)",   text: "#fff" },
  Retoure:   { color: "rgba(255,69,58,0.85)",   text: "#fff" },
};

// ─────────────────────────────────────────────────────────────
// ─────────────────────────────────────────────────────────────
// ManualShipConfirmModal
// ─────────────────────────────────────────────────────────────
// ─────────────────────────────────────────────────────────────
// ManualShipConfirmModal — Doppelte Bestätigung bei manuellem
// Bereit→Versendet (Bypass des Scanner-Workflows).
//
// Drei Hürden, die ALLE überwunden werden müssen, damit der
// Confirm-Button überhaupt klickbar wird:
//   1. Grund auswählen (Pflicht)
//   2. Bei "Anderer Grund": Freitext-Notiz (Pflicht)
//   3. Checkbox "Ich bestätige …" abhaken (Pflicht)
// Das ist die "doppelte Bestätigung" — kein versehentliches Bypassen
// des Scanners möglich. Grund + Datum landen in der Order-Notiz für
// einen sauberen Audit-Trail.
// ─────────────────────────────────────────────────────────────
function ManualShipConfirmModal({ order, reasons, t, dark, F, glass, vh, kbOpen, offsetTop, onClose, onConfirm }) {
  const [reasonId, setReasonId] = useState(null);
  const [otherText, setOtherText] = useState("");
  const [checked, setChecked] = useState(false);

  useEffect(() => {
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => { document.body.style.overflow = prevOverflow; };
  }, []);

  const reason = reasons.find(r => r.id === reasonId);
  const needsText = reasonId === "other";
  const ready = !!reason && checked && (!needsText || otherText.trim().length >= 3);

  return (
    <>
      <div onClick={onClose} style={{
        position: "fixed", inset: 0, zIndex: 92,
        background: dark ? "rgba(0,0,0,0.6)" : "rgba(0,0,0,0.4)",
        backdropFilter: "blur(4px)", WebkitBackdropFilter: "blur(4px)",
        animation: "fadeIn 0.22s cubic-bezier(0.32, 0.72, 0, 1) both",
        willChange: "opacity",
      }} />
      <div onClick={e => e.stopPropagation()} style={modalShellStyle({ vh, kbOpen, offsetTop, glass, F, dark, width: 400, radius: 22, zIndex: 93 })}>
        <ModalHeader
          t={t} dark={dark} F={F}
          title="Manuell bestätigen"
          subtitle={<>#{order.bestellnummer}</>}
          accent={t.accent.orange}
          accentIcon={
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round">
              <path d="M12 9v4"/><path d="M12 17h.01"/>
              <path d="M10.29 3.86 1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z"/>
            </svg>
          }
          onClose={onClose}
        />

        {/* Warning copy */}
        <div style={{ fontSize: 13, color: t.textSub, lineHeight: 1.5, marginBottom: 16, padding: "10px 12px", background: t.accent.orange.weak, borderRadius: 10, border: `1px solid ${t.accent.orange.solid}33` }}>
          Standardmässig sollte das <strong style={{ color: t.text }}>Versandlabel gescannt</strong> werden — das verifiziert die Trackingnummer automatisch. Manuell bestätigen nur in Ausnahmen.
        </div>

        {/* Reason picker */}
        <div style={{ fontSize: 11, color: t.textMuted, fontWeight: 600, letterSpacing: "0.06em", textTransform: "uppercase", marginBottom: 6, paddingLeft: 4 }}>
          Grund <span style={{ color: t.accent.red.solid }}>*</span>
        </div>
        <div style={{ display: "flex", flexDirection: "column", gap: 6, marginBottom: 14 }}>
          {reasons.map(r => {
            const sel = r.id === reasonId;
            return (
              <button key={r.id} onClick={() => setReasonId(r.id)} style={{
                width: "100%", padding: "12px 14px",
                background: sel ? t.accent.blue.weak : (dark ? "rgba(255,255,255,0.04)" : "rgba(0,0,0,0.03)"),
                border: `1.5px solid ${sel ? t.accent.blue.solid : t.glassBorder}`,
                borderRadius: 11, cursor: "pointer", fontFamily: F,
                color: sel ? t.accent.blue.solid : t.text,
                fontSize: 14, fontWeight: sel ? 600 : 500, textAlign: "left",
                display: "flex", alignItems: "center", gap: 10,
                transition: "all 0.16s ease",
              }}>
                <div style={{
                  width: 18, height: 18, borderRadius: "50%", flexShrink: 0,
                  border: `1.5px solid ${sel ? t.accent.blue.solid : t.glassBorder}`,
                  background: sel ? t.accent.blue.solid : "transparent",
                  display: "flex", alignItems: "center", justifyContent: "center",
                }}>
                  {sel && (
                    <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round">
                      <polyline points="20 6 9 17 4 12"/>
                    </svg>
                  )}
                </div>
                {r.label}
              </button>
            );
          })}
        </div>

        {/* Optional Freitext nur bei "Anderer Grund" */}
        {needsText && (
          <>
            <div style={{ fontSize: 11, color: t.textMuted, fontWeight: 600, letterSpacing: "0.06em", textTransform: "uppercase", marginBottom: 6, paddingLeft: 4 }}>
              Begründung <span style={{ color: t.accent.red.solid }}>*</span>
            </div>
            <div style={{ ...glass({ borderRadius: 11 }), padding: "0 14px", display: "flex", alignItems: "center", marginBottom: 14 }}>
              <input
                autoFocus type="text" value={otherText}
                onChange={e => setOtherText(e.target.value)}
                placeholder="Was ist passiert?"
                style={{ flex: 1, background: "transparent", border: "none", outline: "none", padding: "11px 0", fontFamily: F, fontSize: 14, color: t.text, minWidth: 0 }}
              />
            </div>
          </>
        )}

        {/* Checkbox confirm */}
        <button onClick={() => setChecked(c => !c)} style={{
          width: "100%", padding: "12px 14px", marginBottom: 14,
          background: checked ? t.accent.green.weak : (dark ? "rgba(255,255,255,0.04)" : "rgba(0,0,0,0.03)"),
          border: `1.5px solid ${checked ? t.accent.green.solid : t.glassBorder}`,
          borderRadius: 11, cursor: "pointer", fontFamily: F,
          display: "flex", alignItems: "flex-start", gap: 10,
          textAlign: "left",
          transition: "all 0.16s ease",
        }}>
          <div style={{
            width: 20, height: 20, borderRadius: 5, flexShrink: 0, marginTop: 1,
            border: `1.5px solid ${checked ? t.accent.green.solid : t.glassBorder}`,
            background: checked ? t.accent.green.solid : "transparent",
            display: "flex", alignItems: "center", justifyContent: "center",
          }}>
            {checked && (
              <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round">
                <polyline points="20 6 9 17 4 12"/>
              </svg>
            )}
          </div>
          <div style={{ flex: 1, fontSize: 13, color: checked ? t.accent.green.solid : t.text, lineHeight: 1.4, fontWeight: checked ? 600 : 500 }}>
            Ich bestätige, dass diese Bestellung tatsächlich versendet bzw. übergeben wurde.
          </div>
        </button>

        {/* Confirm button */}
        <button
          onClick={() => ready && onConfirm(reason.label, otherText.trim())}
          disabled={!ready}
          style={{
            width: "100%", padding: "14px",
            background: ready ? t.accent.green.solid : (dark ? "rgba(255,255,255,0.06)" : "rgba(0,0,0,0.05)"),
            color: ready ? "#fff" : t.textMuted,
            border: "none", borderRadius: 14,
            fontFamily: F, fontWeight: 700, fontSize: 15, letterSpacing: "-0.1px",
            cursor: ready ? "pointer" : "not-allowed",
            boxShadow: ready ? "0 6px 20px rgba(48,209,88,0.35)" : "none",
            transition: "all 0.18s ease",
          }}
        >Versendet markieren</button>
      </div>
    </>
  );
}

// ─────────────────────────────────────────────────────────────
// OrderDetailGroup — renders all order detail / action overlays:
//   DETAIL MODAL · ADD SHEET · INLINE LABEL · TOAST STACK
//   SENDUNG POPUP · TRACKING CONFIRM · RETOURE · HELFER BANNER · UNRETOURE
// ─────────────────────────────────────────────────────────────
export function OrderDetailGroup({ dark, t, F, glass, inp,
  appVh, appKbOpen, appOffsetTop,
  isDesktop, desktopRightPanel, desktopCenterPopup,
  o, orders, setOrders,
  showDetail, closeDetail,
  showSheet, closeSheet,
  form, setForm, save, inputRef, isDuplicate,
  helperMode, can, currentRole,
  getActiveChannels, getChannelLabel, getStatusLabel, getActiveBatch,
  channels, products, changeOrderChannel, getProductCostPreview,
  updateOrder, setLabelForOrder, openLabel, labelUploadInputRef, pendingLabelOrderIdRef,
  setShipChoice, setDetailDeleteConfirm, detailDeleteConfirm, deleteFromDetail,
  toggleRetoureErhalten,
  sendungOrder, setSendungOrder, confirmNoSendung, setConfirmNoSendung,
  trackingConfirm, setTrackingConfirm, setShowScanner, apiOrderUpdate,
  confirmRetoure, setConfirmRetoure, confirmRetoureNow,
  helperSuccessBanner, setHelperSuccessBanner,
  confirmUnretoure, setConfirmUnretoure, confirmUnretoureNow,
  manualShip, setManualShip, shipChoice }) {
  const toast = useToast();
  // Mobile swipe-back: the whole detail overlay (backdrop + panel) follows the finger
  const { swipeRef: detailSwipeRef, swipeStyle: detailSwipeStyle } = useSwipeBack({
    onBack: closeDetail,
    enabled: !!showDetail && !isDesktop,
  });

  return (
    <>
      {/* ── DETAIL MODAL — iOS-native Smooth-Animation (translate3d, sheetIn, willChange) ── */}
      {showDetail && o && (
        <div ref={!isDesktop ? detailSwipeRef : undefined}
          onClick={isDesktop ? undefined : closeDetail}
          style={isDesktop ? { position: "fixed", inset: 0, pointerEvents: "none", zIndex: 50 } : {
          position: "fixed", inset: 0, zIndex: 50,
          background: dark ? "rgba(0,0,0,0.55)" : "rgba(0,0,0,0.34)",
          backdropFilter: "blur(4px)", WebkitBackdropFilter: "blur(4px)",
          animation: "fadeIn 0.22s cubic-bezier(0.32, 0.72, 0, 1) both",
          willChange: "transform",
          ...detailSwipeStyle,
        }}>
          <div onClick={e => e.stopPropagation()} style={isDesktop ? {
            ...desktopRightPanel,
            ...glass({ boxShadow: dark ? "0 0 48px rgba(0,0,0,0.4)" : "0 0 48px rgba(0,0,0,0.08)" }),
            zIndex: 51, animation: "slideInRight 0.24s cubic-bezier(0.32,0.72,0,1)",
            overflowY: "auto", overflowX: "hidden",
            pointerEvents: "auto",
          } : {
            // Mobile: visualViewport-aware → Tastatur-resistent (kein Whole-Popup-Scroll).
            ...modalShellStyle({ vh: appVh, kbOpen: appKbOpen, offsetTop: appOffsetTop, glass, F, dark, width: 400, radius: 24, padding: 0, zIndex: 51 }),
            overflowX: "hidden",
          }}>
            <div style={{ padding: isDesktop ? "24px 32px 40px" : "20px 22px 22px" }}>

              {/* Header — einheitlich mit allen anderen Modals via ModalHeader */}
              {!isDesktop && (
                <ModalHeader t={t} dark={dark} F={F} title="Bestellung" onClose={closeDetail} marginBottom={18} />
              )}
              {isDesktop && (
                <div style={{ display: "flex", justifyContent: "flex-end", marginBottom: helperMode ? 12 : 18 }}>
                  <ModalCloseButton onClose={closeDetail} dark={dark} />
                </div>
              )}

              {/* Helfer-Kontext-Badge */}
              {helperMode && (
                <div style={{
                  display: "inline-flex", alignItems: "center", gap: 6,
                  padding: "6px 12px", borderRadius: 20, marginBottom: 14,
                  background: o.status === "Bereit" ? "rgba(127,119,221,0.12)" :
                              o.status === "Offen" ? "rgba(255,159,10,0.14)" :
                              o.status === "Versendet" ? "rgba(48,209,88,0.14)" : "rgba(0,0,0,0.06)",
                  color: o.status === "Bereit" ? "var(--accent-troedel)" :
                         o.status === "Offen" ? "var(--accent-orange-strong)" :
                         o.status === "Versendet" ? "var(--accent-stand)" : t.textSub,
                  fontSize: 11, fontWeight: 700, letterSpacing: "0.1em", textTransform: "uppercase",
                }}>
                  {o.status === "Offen"     ? "Etikett kaufen + versenden"
                  : o.status === "Bereit"    ? "Bereit · nur einwerfen"
                  : o.status === "Versendet" ? "Versendet ✓"
                  : "Bestellung"}
                </div>
              )}

              {/* HERO — Bestellnummer groß + Metadata-Zeile */}
              <div style={{ marginBottom: 22 }}>
                {!helperMode ? (
                  <input type="text" value={o.bestellnummer} placeholder="Bestellnummer"
                    onChange={e => updateOrder(o.id, "bestellnummer", e.target.value)}
                    style={{
                      width: "100%", border: "none", background: "transparent",
                      fontFamily: F, fontSize: 32, fontWeight: 700, letterSpacing: "-0.8px",
                      color: t.text, outline: "none", padding: 0, lineHeight: 1.1,
                    }} />
                ) : (
                  <div style={{ fontSize: 32, fontWeight: 700, letterSpacing: "-0.8px", color: t.text, lineHeight: 1.1, wordBreak: "break-all" }}>
                    {o.bestellnummer || "—"}
                  </div>
                )}

                {/* Metadata-Zeile: Status-Chip · Kanal · Datum */}
                <div style={{ display: "flex", alignItems: "center", gap: 10, marginTop: 14, flexWrap: "wrap" }}>
                  {!helperMode && o.status === "Bereit" ? (
                    <button onClick={() => setShipChoice(o)} style={{
                      background: STATUS[o.status]?.bg, color: STATUS[o.status]?.color,
                      border: `1px solid ${STATUS[o.status]?.color}40`, borderRadius: 20,
                      padding: "5px 12px", fontFamily: F, fontWeight: 600, fontSize: 12,
                      cursor: "pointer", flexShrink: 0,
                    }}>{o.status} → Versendet</button>
                  ) : (
                    <span style={{
                      background: STATUS[o.status]?.bg, color: STATUS[o.status]?.color,
                      border: `1px solid ${STATUS[o.status]?.color}40`, borderRadius: 20,
                      padding: "5px 12px", fontFamily: F, fontWeight: 600, fontSize: 12, flexShrink: 0,
                    }}>{getStatusLabel(o.status)}</span>
                  )}
                  <span style={{ fontSize: 13, color: t.textSub, fontWeight: 500 }}>{getChannelLabel(o.channelId) || o.kanal}</span>
                  <span style={{ fontSize: 12, color: t.textMuted }}>·</span>
                  {!helperMode ? (
                    <input type="date" value={o.datum || today()} onChange={e => updateOrder(o.id, "datum", e.target.value)}
                      style={{ border: "none", background: "transparent", fontFamily: F, fontSize: 13, color: t.textSub, outline: "none", padding: 0, fontWeight: 500 }} />
                  ) : (
                    <span style={{ fontSize: 13, color: t.textSub, fontWeight: 500 }}>
                      {(() => { const d = new Date(o.datum); return isNaN(d) ? o.datum : `${String(d.getDate()).padStart(2,'0')}.${String(d.getMonth()+1).padStart(2,'0')}.${d.getFullYear()}`; })()}
                    </span>
                  )}
                </div>
              </div>

              <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
                {!helperMode && orders.some(x => x.id !== o.id && x.bestellnummer.trim() === o.bestellnummer.trim()) && (
                  <div style={{ display: "flex", alignItems: "center", gap: 6, padding: "10px 14px", background: "rgba(255,159,10,0.12)", border: "1px solid rgba(255,159,10,0.3)", borderRadius: 14 }}>
                    <span style={{ fontSize: 14 }}>⚠️</span>
                    <span style={{ fontSize: 13, fontWeight: 600, color: "var(--accent-orange-strong)", fontFamily: F }}>Bestellnummer bereits vorhanden</span>
                  </div>
                )}

                {!helperMode && (
                  <div style={{ ...glass({ borderRadius: 14, padding: 3 }), display: "flex" }}>
                    {getActiveChannels().map(ch => (
                      <button key={ch.id} onClick={() => changeOrderChannel(o.id, ch.id)} style={{
                        flex: 1, padding: "10px 4px", border: "none", cursor: "pointer", fontFamily: F,
                        fontSize: 14, fontWeight: o.channelId === ch.id ? 600 : 500, borderRadius: 11, transition: "all 0.2s ease",
                        background: o.channelId === ch.id ? t.pillActive : "transparent",
                        color: o.channelId === ch.id ? t.text : t.segText,
                        boxShadow: o.channelId === ch.id ? `0 1px 6px rgba(0,0,0,${dark?0.3:0.1})` : "none",
                      }}>{ch.name}</button>
                    ))}
                  </div>
                )}

                {/* Versand-Label — primärer Workflow-Anker */}
                {o.labelKey ? (
                  // Label vorhanden: prominenter Open-Button (für Vater UND Noah)
                  <div onClick={() => openLabel(o.labelKey)} style={{
                    ...glass({ borderRadius: 16, border: "1px solid rgba(48,209,88,0.35)" }),
                    padding: "14px 14px", display: "flex", alignItems: "center", gap: 10,
                    cursor: "pointer", width: "100%", textAlign: "left", fontFamily: F,
                    transition: "transform 0.12s ease",
                    boxSizing: "border-box", overflow: "hidden",
                  }}
                    onMouseDown={e => e.currentTarget.style.transform = "scale(0.98)"}
                    onMouseUp={e => e.currentTarget.style.transform = "scale(1)"}
                    onTouchStart={e => e.currentTarget.style.transform = "scale(0.98)"}
                    onTouchEnd={e => e.currentTarget.style.transform = "scale(1)"}
                  >
                    <div style={{ flexShrink: 0, width: 40, height: 40, borderRadius: 11, background: "rgba(48,209,88,0.18)", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 20 }}>📄</div>
                    <div style={{ flex: 1, minWidth: 0, overflow: "hidden" }}>
                      <div style={{ fontSize: 11, fontWeight: 700, color: "var(--accent-stand)", letterSpacing: "0.08em", textTransform: "uppercase", marginBottom: 2, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>Versandlabel</div>
                      <div style={{ fontSize: 15, fontWeight: 600, color: t.text, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>Öffnen & drucken ›</div>
                    </div>
                    {!helperMode && (
                      <button onClick={e => {
                        e.stopPropagation();
                        document.getElementById(`label-detail-input-${o.id}`)?.click();
                      }} style={{
                        background: "none", border: "none", cursor: "pointer",
                        color: "var(--accent-online)", fontFamily: F, fontSize: 13, fontWeight: 600, padding: "6px 0", flexShrink: 0,
                      }}>Ersetzen</button>
                    )}
                  </div>
                ) : (
                  // Kein Label: Upload-Button (nur Noah). Vater sieht stattdessen Hinweis.
                  helperMode ? (
                    <div style={{ ...glass({ borderRadius: 16, border: "1px solid rgba(255,69,58,0.3)" }), padding: "12px 16px", display: "flex", alignItems: "center", gap: 8 }}>
                      <span style={{ fontSize: 16 }}>⚠️</span>
                      <span style={{ fontSize: 13, color: "var(--accent-red)", fontWeight: 600 }}>Kein Label hinterlegt — bei Noah nachfragen</span>
                    </div>
                  ) : (
                    <button onClick={() => document.getElementById(`label-detail-input-${o.id}`)?.click()} style={{
                      ...glass({ borderRadius: 16, border: `1px dashed ${t.glassBorder}` }),
                      padding: "16px 16px", display: "flex", alignItems: "center", gap: 12,
                      cursor: "pointer", width: "100%", textAlign: "left", fontFamily: F,
                    }}>
                      <div style={{ flexShrink: 0, width: 44, height: 44, borderRadius: 12, background: dark ? "rgba(255,255,255,0.06)" : "rgba(0,0,0,0.04)", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 22 }}>📤</div>
                      <div style={{ flex: 1 }}>
                        <div style={{ fontSize: 11, fontWeight: 700, color: t.textSub, letterSpacing: "0.08em", textTransform: "uppercase", marginBottom: 2 }}>Versandlabel</div>
                        <div style={{ fontSize: 15, fontWeight: 600, color: "var(--accent-online)" }}>PDF oder Bild hochladen</div>
                      </div>
                    </button>
                  )
                )}

                {/* Hidden input fürs Detail-Panel */}
                {!helperMode && (
                  <input
                    id={`label-detail-input-${o.id}`}
                    type="file"
                    accept="application/pdf,image/*"
                    style={{ display: "none" }}
                    onChange={e => {
                      const f = e.target.files?.[0];
                      if (f) setLabelForOrder(o.id, f);
                      e.target.value = "";
                    }}
                  />
                )}

                {/* Helfer-Bestätigung: Paket eingeworfen → Versendet */}
                {helperMode && o.status === "Bereit" && (
                  <button onClick={() => setShipChoice(o)} style={{
                    marginTop: 4,
                    background: "linear-gradient(180deg, #30D158 0%, #28b94c 100%)",
                    border: "1px solid rgba(48,209,88,0.5)",
                    borderRadius: 18, padding: "18px 18px",
                    display: "flex", alignItems: "center", justifyContent: "center", gap: 10,
                    cursor: "pointer", color: "#fff", fontFamily: F, fontWeight: 700, fontSize: 16,
                    width: "100%", boxShadow: "0 6px 20px rgba(48,209,88,0.35), inset 0 1px 0 rgba(255,255,255,0.25)",
                    transition: "transform 0.16s cubic-bezier(0.34,1.2,0.64,1)",
                    letterSpacing: "-0.2px",
                  }}
                    onMouseDown={e => e.currentTarget.style.transform = "scale(0.97)"}
                    onMouseUp={e => e.currentTarget.style.transform = "scale(1)"}
                    onMouseLeave={e => e.currentTarget.style.transform = "scale(1)"}
                    onTouchStart={e => e.currentTarget.style.transform = "scale(0.97)"}
                    onTouchEnd={e => e.currentTarget.style.transform = "scale(1)"}
                  >
                    <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
                      <path d="M4 10.5L8 14.5L16 6" stroke="#fff" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round"/>
                    </svg>
                    Paket eingeworfen
                  </button>
                )}

                {/* Sendungsnummer — optional, nur für Noah, rein informativ.
                    Workflow basiert jetzt auf Label-PDF, nicht auf Sendungsnummer. */}

                {!helperMode && (
                <div style={{ ...glass({ borderRadius: 16 }), overflow: "hidden" }}>
                  <input type="text" value={o.notiz || ""} placeholder="Notiz…"
                    onChange={e => updateOrder(o.id, "notiz", e.target.value)}
                    style={inp({ padding: "14px 16px" })} />
                </div>
                )}

                {!helperMode && o.status === "Retoure" && (
                  <button onClick={() => toggleRetoureErhalten(o.id)} style={{ ...glass({ borderRadius: 16, border: `1px solid ${o.retoureErhalten ? "rgba(48,209,88,0.3)" : "rgba(255,69,58,0.25)"}` }), padding: "14px 16px", display: "flex", justifyContent: "space-between", alignItems: "center", cursor: "pointer", width: "100%", transition: "all 0.2s ease" }}>
                    <span style={{ fontSize: 13, color: t.textSub, fontWeight: 500 }}>Retoure erhalten</span>
                    <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                      <span style={{ fontSize: 13, fontWeight: 600, color: o.retoureErhalten ? "var(--accent-stand)" : "var(--accent-red)" }}>{o.retoureErhalten ? "Ja" : "Ausstehend"}</span>
                      <div style={{ width: 22, height: 22, borderRadius: "50%", background: o.retoureErhalten ? "var(--accent-stand)" : "transparent", border: o.retoureErhalten ? "none" : "1.5px solid rgba(255,69,58,0.4)", display: "flex", alignItems: "center", justifyContent: "center", transition: "all 0.2s" }}>
                        {o.retoureErhalten && <svg width="11" height="9" viewBox="0 0 11 9" fill="none"><path d="M1 4.5L4 7.5L10 1" stroke="white" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"/></svg>}
                      </div>
                    </div>
                  </button>
                )}

                {!helperMode && (currentRole === null || can("delete")) && (!detailDeleteConfirm ? (
                  <button onClick={() => setDetailDeleteConfirm(true)} style={{
                    marginTop: 16, background: "transparent", border: "none",
                    cursor: "pointer", color: "var(--accent-red)", fontFamily: F, fontWeight: 500, fontSize: 13,
                    width: "100%", padding: "10px", textAlign: "center",
                    transition: "opacity 0.16s ease", opacity: 0.7,
                  }}
                    onMouseEnter={e => e.currentTarget.style.opacity = "1"}
                    onMouseLeave={e => e.currentTarget.style.opacity = "0.7"}
                  >Bestellung löschen</button>
                ) : (
                  <div style={{ marginTop: 16, ...glass({ borderRadius: 16, border: `1px solid ${t.deleteBdr}` }), padding: "14px 16px" }}>
                    <div style={{ fontSize: 13, color: t.textSub, textAlign: "center", marginBottom: 12 }}>Wirklich löschen?</div>
                    <div style={{ display: "flex", gap: 8 }}>
                      <button onClick={() => setDetailDeleteConfirm(false)} style={{ flex: 1, background: t.btnGhost, border: "none", borderRadius: 12, padding: "11px", fontFamily: F, fontWeight: 600, fontSize: 14, cursor: "pointer", color: t.btnGhostTxt }}>Abbrechen</button>
                      <button onClick={() => deleteFromDetail(o.id)} style={{ flex: 1, background: "var(--accent-red)", border: "none", borderRadius: 12, padding: "11px", fontFamily: F, fontWeight: 600, fontSize: 14, cursor: "pointer", color: "#fff" }}>Löschen</button>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      )}
      {/* ── ADD SHEET ── Mobile: v2-Bottom-Sheet (von unten, Grabber, scrollbarer Body),
          feste Position (bottom: 0). Tastatur ueberlagert das Sheet, kein Lift/Springen.
          Desktop bleibt zentriert wie vorher. */}
      {showSheet && (
        <>
          <div onClick={closeSheet} style={{
            position: "fixed", inset: 0,
            background: dark ? "rgba(0,0,0,0.5)" : "rgba(0,0,0,0.32)",
            zIndex: Z.sheetBackdrop,
            backdropFilter: "blur(4px)", WebkitBackdropFilter: "blur(4px)",
            animation: "fadeIn 0.22s cubic-bezier(0.32, 0.72, 0, 1) both",
            willChange: "opacity",
          }} />
          <div
            style={isDesktop ? {
              ...desktopCenterPopup,
              ...glass({ borderRadius: 24, boxShadow: dark ? "0 24px 64px rgba(0,0,0,0.5)" : "0 24px 64px rgba(0,0,0,0.18)" }),
              zIndex: Z.sheet, animation: "modalIn 0.26s cubic-bezier(0.34,1.2,0.64,1)",
              overflowY: "auto",
            } : {
              position: "fixed", left: 0, right: 0, bottom: 0, zIndex: Z.sheet,
              display: "flex", flexDirection: "column",
              maxHeight: "92vh",
              background: dark ? "#2C2C2E" : "#FFFFFF",
              borderRadius: "24px 24px 0 0",
              animation: "slideUpSheet 0.3s cubic-bezier(0.32, 0.72, 0, 1)",
              paddingBottom: "env(safe-area-inset-bottom, 0px)",
            }}
          >
            {!isDesktop && (
              <>
                <div style={{ display: "flex", justifyContent: "center", paddingTop: 10, paddingBottom: 2 }}>
                  <div className="bottom-sheet-handle" />
                </div>
                <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", padding: "2px 20px 12px" }}>
                  <span style={{ fontFamily: F, fontSize: 20, fontWeight: 600, color: t.text }}>Neue Bestellung</span>
                  <button onClick={closeSheet} aria-label="Schliessen" style={{ background: "none", border: "none", cursor: "pointer", color: t.textMuted, fontSize: 24, lineHeight: 1, padding: 4 }}>×</button>
                </div>
              </>
            )}
            <div style={isDesktop ? { padding: "24px" } : { flex: 1, overflowY: "auto", padding: "0 20px 20px", overscrollBehavior: "contain", WebkitOverflowScrolling: "touch" }}>
              {isDesktop && (
                <ModalHeader t={t} dark={dark} F={F} title="Neue Bestellung" onClose={closeSheet} marginBottom={18} />
              )}

              <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 14 }}>
                <input ref={inputRef} type="text" value={form.bestellnummer} placeholder="Bestellnummer" autoComplete="off"
                  onChange={e => setForm(v => ({ ...v, bestellnummer: e.target.value }))}
                  onKeyDown={e => e.key === "Enter" && save()}
                  className={inputCls} style={{ flex: 1, minWidth: 0 }} />
                <button onClick={() => { inputRef.current?.focus(); document.execCommand('paste'); }}
                  style={{ background: "none", border: "none", cursor: "pointer", color: "var(--accent-online)", fontFamily: F, fontSize: 14, fontWeight: 600, padding: "0 6px", flexShrink: 0, whiteSpace: "nowrap" }}>
                  Einfügen
                </button>
              </div>

              {/* Kanal-Auswahl (neue channels-Tabelle, Online-Store Umbau Phase 1) */}
              <div style={{ ...glass({ borderRadius: 14, padding: 3 }), display: "flex", flexWrap: "wrap", gap: 4, marginBottom: 14 }}>
                {(channels || []).map(ch => (
                  <button key={ch.id} onMouseDown={e => e.preventDefault()} onClick={() => { setForm(v => ({ ...v, channelId: ch.id })); inputRef.current?.focus(); }} style={{
                    flex: "1 1 auto", padding: "8px 10px", border: "none", cursor: "pointer", fontFamily: F,
                    fontSize: 14, fontWeight: form.channelId === ch.id ? 600 : 400, borderRadius: 11, transition: "background 0.18s ease, color 0.18s ease",
                    background: form.channelId === ch.id ? t.pillActive : t.segInactive,
                    color: form.channelId === ch.id ? t.text : t.segText,
                  }}>{ch.name}</button>
                ))}
                {(channels || []).length === 0 && (
                  <span style={{ fontSize: 13, color: t.textMuted, fontFamily: F, padding: "8px 10px" }}>Kein Kanal vorhanden</span>
                )}
              </div>

              {/* Produktzeilen (Produkt + Menge), mehrere möglich (Phase 1) */}
              <div style={{ marginBottom: 14 }}>
                <div style={{ fontSize: 11, fontWeight: 600, color: t.textMuted, textTransform: "uppercase", letterSpacing: "0.06em", marginBottom: 8 }}>Produkte</div>
                {(form.items || []).map((line, idx) => {
                  const activeProducts = (products || []).filter(p => !p.archivedAt);
                  const lineProduct = (products || []).find(p => p.id === line.productId);
                  const lineSubtotal = (Number(line.quantity) || 0) * (lineProduct?.price || 0);
                  return (
                    <div key={idx} style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 8 }}>
                      <select value={line.productId || ""} onMouseDown={e => e.stopPropagation()}
                        onChange={e => setForm(v => ({ ...v, items: v.items.map((it, i) => i === idx ? { ...it, productId: e.target.value } : it) }))}
                        className={inputCls} style={{ flex: 1, minWidth: 0 }}>
                        {activeProducts.length === 0 && <option value="">— kein Produkt —</option>}
                        {activeProducts.map(p => <option key={p.id} value={p.id}>{p.name}</option>)}
                      </select>
                      <input type="number" min="1" value={line.quantity}
                        onChange={e => setForm(v => ({ ...v, items: v.items.map((it, i) => i === idx ? { ...it, quantity: Math.max(1, parseInt(e.target.value) || 1) } : it) }))}
                        className={inputCls} style={{ width: 60, textAlign: "center", paddingLeft: 6, paddingRight: 6, flexShrink: 0 }} />
                      <span style={{ minWidth: 56, textAlign: "right", fontFamily: F, fontSize: 13, fontWeight: 600, color: t.textMuted, flexShrink: 0 }}>{fmtCurrency(lineSubtotal)}</span>
                      {(form.items || []).length > 1 && (
                        <button onMouseDown={e => e.preventDefault()} onClick={() => setForm(v => ({ ...v, items: v.items.filter((_, i) => i !== idx) }))}
                          style={{ border: "none", background: "transparent", color: "var(--accent-red)", cursor: "pointer", fontSize: 20, lineHeight: 1, padding: "0 2px", flexShrink: 0 }}>×</button>
                      )}
                    </div>
                  );
                })}
                <button onMouseDown={e => e.preventDefault()}
                  onClick={() => { const ap = (products || []).filter(p => !p.archivedAt); const pid = ap[0]?.id || ""; setForm(v => ({ ...v, items: [...(v.items || []), { productId: pid, quantity: 1 }] })); }}
                  style={{ width: "100%", border: `1px dashed ${t.segText}55`, background: "transparent", borderRadius: 12, padding: "10px", fontFamily: F, fontSize: 14, fontWeight: 600, color: "var(--accent-online)", cursor: "pointer" }}>
                  + Produkt hinzufügen
                </button>
              </div>

              {/* Gewinn-Aufschluesselung inkl. FIFO-Wareneinsatz (getProductCostPreview spiegelt
                  consumeBatchesFIFO → Vorschau == gespeicherter Snapshot). Fehlende EK = 0 (nicht raten). */}
              {(() => {
                const items = (form.items || []).filter(it => it.productId && (Number(it.quantity) || 0) > 0);
                if (items.length === 0) return null;
                const ch = (channels || []).find(c => c.id === form.channelId);
                let revenue = 0, cogs = 0, anyUnknown = false;
                // Mengen pro Produkt aggregieren: FIFO über die Gesamtmenge = exakt der
                // sequentielle Server-Verbrauch (gleiches Produkt in mehreren Zeilen).
                const qtyByProduct = {};
                for (const it of items) {
                  const p = (products || []).find(x => x.id === it.productId);
                  const qty = Number(it.quantity) || 0;
                  revenue += qty * (p?.price || 0);
                  qtyByProduct[it.productId] = (qtyByProduct[it.productId] || 0) + qty;
                }
                for (const pid of Object.keys(qtyByProduct)) {
                  const c = getProductCostPreview ? getProductCostPreview(pid, qtyByProduct[pid]) : { totalCost: 0, shortfall: 0 };
                  cogs += c.totalCost || 0;
                  if ((c.shortfall || 0) > 0) anyUnknown = true;
                }
                const feePct = ch?.feePercent || 0;
                const fee = revenue * feePct / 100;
                const shipping = ch?.shippingCost || 0;
                const profit = revenue - fee - cogs - shipping;
                const margin = revenue > 0 ? Math.round((profit / revenue) * 100) : 0;
                const profitColor = profit >= 0 ? "var(--accent-green)" : "var(--accent-red)";
                const Row = ({ label, value, strong }) => (
                  <div style={{ display: "flex", justifyContent: "space-between", alignItems: "baseline" }}>
                    <span style={{ fontFamily: F, fontSize: 13, color: t.textMuted }}>{label}</span>
                    <span style={{ fontFamily: F, fontSize: 14, fontWeight: strong ? 700 : 600, color: t.text, fontVariantNumeric: "tabular-nums" }}>{value}</span>
                  </div>
                );
                return (
                  <div data-testid="order-live-total" style={{ ...glass({ borderRadius: 12 }), padding: "12px 14px", marginBottom: 12, display: "flex", flexDirection: "column", gap: 7 }}>
                    <Row label="Umsatz" value={fmtCurrency(revenue)} strong />
                    <Row label={`Kanal-Gebühr (${feePct} %)`} value={`− ${fmtCurrency(fee)}`} />
                    <Row label="Wareneinsatz (EK)" value={`− ${fmtCurrency(cogs)}`} />
                    {anyUnknown && (
                      <div style={{ fontFamily: F, fontSize: 11, color: t.textMuted, marginTop: -3 }}>
                        EK teilweise unbekannt — fehlende Chargen zählen als 0
                      </div>
                    )}
                    {shipping > 0 && <Row label="Versand" value={`− ${fmtCurrency(shipping)}`} />}
                    <div style={{ height: 1, background: "var(--border-default)", margin: "3px 0" }} />
                    <div style={{ display: "flex", justifyContent: "space-between", alignItems: "baseline" }}>
                      <span style={{ fontFamily: F, fontSize: 15, fontWeight: 700, color: t.text }}>Gewinn</span>
                      <span style={{ fontFamily: F, fontSize: 22, fontWeight: 800, color: profitColor, fontVariantNumeric: "tabular-nums", letterSpacing: "-0.01em" }}>{fmtCurrency(profit)}</span>
                    </div>
                    <div style={{ display: "flex", justifyContent: "flex-end" }}>
                      <span style={{ fontFamily: F, fontSize: 12, color: t.textMuted }}>Marge {margin} %</span>
                    </div>
                  </div>
                );
              })()}

              {isDuplicate && (
                <div style={{ display: "flex", alignItems: "center", gap: 6, padding: "8px 12px", background: "color-mix(in srgb, var(--accent-orange-strong) 12%, transparent)", border: "1px solid color-mix(in srgb, var(--accent-orange-strong) 30%, transparent)", borderRadius: 12, marginBottom: 2 }}>
                  <span style={{ fontSize: 14 }}>⚠️</span>
                  <span style={{ fontSize: 13, fontWeight: 600, color: "var(--accent-orange-strong)", fontFamily: F }}>Bestellnummer bereits vorhanden</span>
                </div>
              )}

              {(() => {
                const hasItems = (form.items || []).some(it => it.productId && (Number(it.quantity) || 0) > 0);
                const enabled = form.bestellnummer && !isDuplicate && hasItems && !!form.channelId;
                return (
                  <button onClick={save} disabled={!enabled}
                    /* preventDefault → Klick blurt das Input NICHT, iOS-Tastatur bleibt offen */
                    onMouseDown={e => e.preventDefault()}
                    onTouchStart={e => e.preventDefault()}
                    className={enabled ? "text-white" : ""}
                    style={{
                    width: "100%", border: "none", borderRadius: 14, padding: "16px", fontFamily: F, fontWeight: 600, fontSize: 17,
                    marginTop: 4,
                    cursor: enabled ? "pointer" : "not-allowed",
                    background: enabled ? "var(--accent-online)" : t.btnGhost,
                    color: enabled ? undefined : t.textMuted,
                    transition: "background 0.2s ease",
                  }}>Hinzufügen</button>
                );
              })()}
            </div>
          </div>
        </>
      )}
      {/* ── INLINE LABEL UPLOAD (für Offen-Pille → direkter Datei-Picker ohne Detail-Modal) ── */}
      <input
        ref={labelUploadInputRef}
        type="file"
        accept="image/*,application/pdf"
        style={{ display: "none" }}
        onChange={async e => {
          const f = e.target.files?.[0];
          const id = pendingLabelOrderIdRef.current;
          e.target.value = "";
          pendingLabelOrderIdRef.current = null;
          if (f && id) await setLabelForOrder(id, f);
        }}
      />
      {/* ── SENDUNG MINI POPUP ── */}
      {sendungOrder && (() => {
        const sOrder = orders.find(x => x.id === sendungOrder.id) || sendungOrder;
        const tryClose = () => {
          if (!sOrder.trackingNumber || !sOrder.trackingNumber.trim()) {
            setConfirmNoSendung(true);
          } else {
            setSendungOrder(null);
            window.scrollTo({ top: 0, behavior: "smooth" });
          }
        };
        return (
          <>
            <div onClick={tryClose} style={{
              position: "fixed", inset: 0, zIndex: 50,
              background: dark ? "rgba(0,0,0,0.55)" : "rgba(0,0,0,0.34)",
              backdropFilter: "blur(4px)", WebkitBackdropFilter: "blur(4px)",
              animation: "fadeIn 0.22s cubic-bezier(0.32, 0.72, 0, 1) both",
              willChange: "opacity",
            }} />
            <div onClick={e => e.stopPropagation()} style={isDesktop ? {
              ...modalShellStyle({ vh: appVh, kbOpen: appKbOpen, offsetTop: appOffsetTop, glass, F, dark, width: 460, radius: 24, padding: "20px 22px 22px", zIndex: 51 }),
            } : {
              ...bottomSheetShellStyle({ vh: appVh, kbOpen: appKbOpen, offsetTop: appOffsetTop, F, dark, width: 560, padding: "0 22px 22px", zIndex: 51 }),
            }}>
              <div style={{ padding: isDesktop ? 0 : "0 0 4px" }}>
                {!isDesktop ? (
                  <BottomSheetHeader t={t} dark={dark} F={F} title={`#${sOrder.bestellnummer}`} subtitle={sOrder.status} onClose={tryClose} marginBottom={16} />
                ) : (
                  <ModalHeader t={t} dark={dark} F={F} title={`#${sOrder.bestellnummer}`} subtitle={sOrder.status} onClose={tryClose} marginBottom={16} />
                )}

                <div style={{
                  ...glass({
                    borderRadius: 16,
                    border: sOrder.status === "Bereit"
                      ? "1px solid rgba(127,119,221,0.35)"
                      : "1px solid rgba(48,209,88,0.35)",
                  }),
                  padding: "4px 12px 4px 16px", display: "flex", alignItems: "center", gap: 8, overflow: "hidden",
                }}>
                  <span style={{ fontSize: 16, flexShrink: 0 }}>📦</span>
                  <input
                    autoFocus
                    id="sendung-mini-input"
                    type="text"
                    value={sOrder.trackingNumber || ""}
                    placeholder="Sendungsnummer eingeben…"
                    onChange={e => updateOrder(sOrder.id, "trackingNumber", e.target.value)}
                    onKeyDown={e => { if (e.key === "Enter") tryClose(); }}
                    style={{ flex: 1, minWidth: 0, border: "none", background: "transparent", fontFamily: F, fontSize: 17, fontWeight: 500, color: sOrder.trackingNumber ? "var(--accent-stand)" : t.text, outline: "none", padding: "14px 0" }}
                  />
                  <button onClick={() => { const el = document.getElementById("sendung-mini-input"); el?.focus(); document.execCommand("paste"); }}
                    style={{ background: "none", border: "none", cursor: "pointer", color: "var(--accent-online)", fontFamily: F, fontSize: 13, fontWeight: 600, padding: 0, flexShrink: 0 }}>
                    Einfügen
                  </button>
                </div>
                <div style={{ fontSize: 12, color: t.textMuted, marginTop: 10, paddingLeft: 4, lineHeight: 1.4 }}>
                  Bei Selbstabholung kannst du auch ohne Sendungsnummer fortfahren.
                </div>
              </div>
            </div>

            {/* Confirmation when no sendungsnummer */}
            {confirmNoSendung && (
              <>
                <div onClick={() => setConfirmNoSendung(false)} style={{
                  position: "fixed", inset: 0, zIndex: 90,
                  background: dark ? "rgba(0,0,0,0.55)" : "rgba(0,0,0,0.34)",
                  backdropFilter: "blur(4px)", WebkitBackdropFilter: "blur(4px)",
                  animation: "fadeIn 0.22s cubic-bezier(0.32, 0.72, 0, 1) both",
                  willChange: "opacity",
                }} />
                <div onClick={e => e.stopPropagation()} style={modalShellStyle({ vh: appVh, kbOpen: appKbOpen, offsetTop: appOffsetTop, glass, F, dark, width: 360, radius: 24, zIndex: 91 })}>
                  <ModalHeader
                    t={t} dark={dark} F={F}
                    title="Keine Sendungsnummer"
                    subtitle="Bestellung trotzdem als versendet markieren?"
                    accent={t.accent.orange}
                    accentIcon={
                      <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round">
                        <path d="M12 9v4"/><path d="M12 17h.01"/>
                        <path d="M10.29 3.86 1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z"/>
                      </svg>
                    }
                    onClose={() => setConfirmNoSendung(false)}
                  />
                  <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
                    <button onClick={() => {
                      setConfirmNoSendung(false);
                      // Force keyboard reopen on iOS — focus immediately during user gesture
                      const el = document.getElementById("sendung-mini-input");
                      if (el) {
                        el.focus();
                        requestAnimationFrame(() => el.focus());
                        setTimeout(() => el.focus(), 50);
                        setTimeout(() => el.focus(), 200);
                      }
                    }} style={{
                      width: "100%", background: "rgba(55,138,221,0.85)", border: "none", borderRadius: 12,
                      padding: "13px", fontFamily: F, fontWeight: 600, fontSize: 15, cursor: "pointer", color: "#fff",
                      boxShadow: "0 4px 20px rgba(55,138,221,0.3)",
                    }}>Sendungsnummer eingeben</button>
                    {!helperMode && (
                    <button onClick={() => {
                      // Trotzdem als versendet: Status auf Versendet setzen (auch ohne Sendungsnummer)
                      const id = sendungOrder?.id;
                      if (id) {
                        setOrders(o => o.map(x => x.id !== id ? x : { ...x, status: "Versendet" }));
                        apiOrderUpdate(id, { status: "Versendet" });
                      }
                      setConfirmNoSendung(false);
                      setSendungOrder(null);
                      window.scrollTo({ top: 0, behavior: "smooth" });
                    }} style={{
                      width: "100%", background: dark ? "rgba(255,255,255,0.08)" : "rgba(0,0,0,0.05)", border: "none", borderRadius: 12,
                      padding: "11px", fontFamily: F, fontWeight: 500, fontSize: 14, cursor: "pointer", color: t.textSub,
                    }}>Trotzdem als versendet</button>
                    )}
                    <button onClick={() => {
                      // Cancel — revert status back to Offen
                      const id = sendungOrder?.id;
                      if (id) {
                        setOrders(o => o.map(x => x.id !== id ? x : { ...x, status: "Offen" }));
                        apiOrderUpdate(id, { status: "Offen" });
                      }
                      setConfirmNoSendung(false);
                      setSendungOrder(null);
                      window.scrollTo({ top: 0, behavior: "smooth" });
                    }} style={{
                      width: "100%", background: "transparent", border: "none", borderRadius: 12,
                      padding: "9px", fontFamily: F, fontWeight: 500, fontSize: 13, cursor: "pointer", color: "var(--accent-red)",
                    }}>Abbrechen (zurück zu Offen)</button>
                  </div>
                </div>
              </>
            )}
          </>
        );
      })()}
      {/* ── TRACKING-CONFIRM MODAL (OCR) ── */}
      {trackingConfirm && (() => {
        const tc = trackingConfirm;
        const cands = tc.candidates || [];
        const order = orders.find(x => x.id === tc.orderId);
        const friendlyName = (n) => ({
          keyword: "Sendungs-Keyword",
          dhl: "DHL-Format",
          dhl_intl: "DHL Int.",
          dpd: "DPD",
          gls: "GLS",
          hermes: "Hermes",
          deutschepost: "Dt. Post",
          ups: "UPS",
          fedex: "FedEx",
          usps: "USPS",
          numeric_long: "Lange Zahl",
          alpha: "Alphanumerisch",
        }[n] || n || "?");
        const confirm = async (value) => {
          const v = (value || "").trim();
          if (!v) { toast.warning("Bitte Trackingnummer eingeben oder wählen"); return; }
          setTrackingConfirm(null);
          setOrders(o => o.map(x => x.id === tc.orderId ? { ...x, trackingNumber: v } : x));
          try {
            await apiOrderUpdate(tc.orderId, { trackingNumber: v });
            haptic(15);
            toast.success(`Trackingnummer: ${v} ✓`);
          } catch (e) {
            console.warn("Tracking-Update fehlgeschlagen:", e);
            toast.error("Speichern fehlgeschlagen");
          }
        };
        const skip = () => {
          setTrackingConfirm(null);
          toast.info("Trackingnummer kann später manuell eingetragen werden");
        };
        return (
          <>
            <div onClick={skip} style={{
              position: "fixed", inset: 0, zIndex: 80,
              background: dark ? "rgba(0,0,0,0.55)" : "rgba(0,0,0,0.34)",
              backdropFilter: "blur(4px)", WebkitBackdropFilter: "blur(4px)",
              animation: "fadeIn 0.22s cubic-bezier(0.32, 0.72, 0, 1) both",
              willChange: "opacity",
            }} />
            <div onClick={e => e.stopPropagation()} style={bottomSheetShellStyle({ vh: appVh, kbOpen: appKbOpen, offsetTop: appOffsetTop, F, dark, width: 560, padding: "0 22px 22px", zIndex: 81 })}>
              <BottomSheetHeader t={t} dark={dark} F={F} title="Trackingnummer bestätigen" onClose={skip} marginBottom={14} />
              <div style={{ fontSize: 13, color: t.textSub, marginBottom: 14, lineHeight: 1.4 }}>
                {order?.name ? <>Für <b style={{ color: t.text }}>{order.name}</b>: w</> : "W"}
                ähle die korrekte Sendungsnummer aus oder gib sie manuell ein.
              </div>

              {/* Candidate-Liste */}
              {cands.length > 0 && (
                <div style={{ marginBottom: 14 }}>
                  <div style={{ fontSize: 10, fontWeight: 700, color: t.textMuted, letterSpacing: "0.08em", textTransform: "uppercase", marginBottom: 8, paddingLeft: 4 }}>
                    Erkannte Kandidaten
                  </div>
                  <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
                    {cands.map((c, i) => (
                      <button key={`${c.value}-${i}`}
                        onClick={() => confirm(c.value)}
                        style={{
                          ...glass({ borderRadius: 14 }),
                          border: i === 0 ? "1.5px solid rgba(55,138,221,0.5)" : `1px solid ${t.glassBorder}`,
                          background: i === 0 ? "rgba(55,138,221,0.08)" : (dark ? "rgba(255,255,255,0.04)" : "rgba(255,255,255,0.5)"),
                          padding: "12px 14px",
                          textAlign: "left",
                          cursor: "pointer",
                          fontFamily: F,
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "space-between",
                          gap: 10,
                        }}>
                        <div style={{ flex: 1, minWidth: 0 }}>
                          <div style={{ fontSize: 16, fontWeight: 600, color: t.text, fontFamily: "ui-monospace, SF Mono, Menlo, monospace", letterSpacing: "0.5px", overflow: "hidden", textOverflow: "ellipsis" }}>
                            {c.value}
                          </div>
                          <div style={{ fontSize: 11, color: t.textMuted, marginTop: 2 }}>
                            {friendlyName(c.name)}{i === 0 ? " · Beste Schätzung" : ""}
                          </div>
                        </div>
                        <div style={{ fontSize: 18, color: "var(--accent-online)", flexShrink: 0 }}>›</div>
                      </button>
                    ))}
                  </div>
                </div>
              )}

              {/* Manuelle Eingabe */}
              <div style={{ marginBottom: 14 }}>
                <div style={{ fontSize: 10, fontWeight: 700, color: t.textMuted, letterSpacing: "0.08em", textTransform: "uppercase", marginBottom: 8, paddingLeft: 4 }}>
                  Manuell eingeben
                </div>
                <div style={{ ...glass({ borderRadius: 14 }), padding: "0 14px", display: "flex", alignItems: "center" }}>
                  <input
                    type="text"
                    value={tc.manualValue}
                    onChange={e => setTrackingConfirm(s => s ? { ...s, manualValue: e.target.value } : s)}
                    placeholder="Trackingnummer eintippen…"
                    autoComplete="off"
                    autoCapitalize="characters"
                    spellCheck={false}
                    style={{
                      flex: 1, minWidth: 0, border: "none", background: "transparent",
                      fontFamily: "ui-monospace, SF Mono, Menlo, monospace",
                      fontSize: 16, fontWeight: 500, color: t.text, outline: "none",
                      padding: "14px 0", letterSpacing: "0.5px",
                    }}
                  />
                </div>
              </div>

              {/* OCR-Text Debug-Toggle */}
              {tc.ocrText && (
                <details style={{ marginBottom: 14 }}>
                  <summary style={{ fontSize: 11, color: t.textMuted, cursor: "pointer", padding: "6px 4px", userSelect: "none" }}>
                    OCR-Text anzeigen (Debug)
                  </summary>
                  <pre style={{
                    ...glass({ borderRadius: 10 }),
                    padding: "10px 12px",
                    marginTop: 6,
                    fontSize: 10,
                    color: t.textSub,
                    fontFamily: "ui-monospace, SF Mono, Menlo, monospace",
                    whiteSpace: "pre-wrap",
                    wordBreak: "break-word",
                    maxHeight: 140,
                    overflowY: "auto",
                  }}>{tc.ocrText}</pre>
                </details>
              )}

              {/* Action-Buttons */}
              <div style={{ display: "flex", gap: 10 }}>
                <button onClick={skip} style={{
                  flex: 1,
                  ...glass({ borderRadius: 14 }),
                  border: `1px solid ${t.glassBorder}`,
                  padding: "14px",
                  fontFamily: F, fontWeight: 500, fontSize: 14, cursor: "pointer", color: t.textSub,
                }}>Überspringen</button>
                <button onClick={() => confirm(tc.manualValue)} style={{
                  flex: 1.4,
                  background: "rgba(55,138,221,0.85)", border: "none", borderRadius: 14,
                  padding: "14px", fontFamily: F, fontWeight: 600, fontSize: 15, cursor: "pointer", color: "#fff",
                  boxShadow: "0 4px 20px rgba(55,138,221,0.3)",
                }}>Bestätigen</button>
              </div>
            </div>
          </>
        );
      })()}
      {/* ── RETOURE CONFIRM ── */}
      {confirmRetoure !== null && (() => {
        const order = orders.find(o => o.id === confirmRetoure);
        if (!order) return null;
        return (
          <>
            <div onClick={() => setConfirmRetoure(null)} style={{
              position: "fixed", inset: 0, zIndex: 90,
              background: dark ? "rgba(0,0,0,0.55)" : "rgba(0,0,0,0.34)",
              backdropFilter: "blur(4px)", WebkitBackdropFilter: "blur(4px)",
              animation: "fadeIn 0.22s cubic-bezier(0.32, 0.72, 0, 1) both",
              willChange: "opacity",
            }} />
            <div onClick={e => e.stopPropagation()} style={modalShellStyle({ vh: appVh, kbOpen: appKbOpen, offsetTop: appOffsetTop, glass, F, dark, width: 360, radius: 22, zIndex: 91 })}>
              <ModalHeader
                t={t} dark={dark} F={F}
                title="Retoure markieren?"
                subtitle={<>#{order.bestellnummer}</>}
                accent={t.accent.orange}
                accentIcon={
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M12 9v4"/><path d="M12 17h.01"/>
                    <path d="M10.29 3.86 1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z"/>
                  </svg>
                }
                onClose={() => setConfirmRetoure(null)}
              />
              <div style={{ fontSize: 13, color: t.textSub, lineHeight: 1.5, marginBottom: 16, padding: "10px 12px", background: t.accent.orange.weak, borderRadius: 10, border: `1px solid ${t.accent.orange.solid}33` }}>
                Diese Bestellung ist als <strong style={{ color: t.text }}>versendet</strong> markiert. Eine Retoure setzt sie zurück und entfernt sie aus dem Umsatz.
              </div>
              <button onClick={() => setConfirmRetoure(null)} style={{
                width: "100%", padding: "13px", marginBottom: 8,
                background: "rgba(55,138,221,0.92)", border: "none", borderRadius: 12,
                fontFamily: F, fontWeight: 700, fontSize: 15, cursor: "pointer", color: "#fff",
                letterSpacing: "-0.1px", boxShadow: "0 4px 20px rgba(55,138,221,0.3)",
              }}>Abbrechen</button>
              <button onClick={confirmRetoureNow} style={{
                width: "100%", padding: "11px",
                background: "transparent", border: "none",
                fontFamily: F, fontWeight: 500, fontSize: 13, cursor: "pointer", color: t.accent.red.solid,
              }}>Trotzdem als Retoure setzen</button>
            </div>
          </>
        );
      })()}

      {/* ─────────────────────────────────────────────────────────
          BEREIT → VERSENDET — Stufe 1: ShipChoiceModal
          User soll standardmässig den Scanner nutzen. Manuell nur als
          Ausnahme (Persönliche Übergabe etc.). Beide Pfade führen zu
          status: "Versendet", aber der manuelle Pfad braucht eine
          doppelte Bestätigung in einem zweiten Modal.
          ───────────────────────────────────────────────────────── */}
      {shipChoice && (
        <>
          <div onClick={() => setShipChoice(null)} style={{
            position: "fixed", inset: 0, zIndex: 90,
            background: dark ? "rgba(0,0,0,0.55)" : "rgba(0,0,0,0.34)",
            backdropFilter: "blur(4px)", WebkitBackdropFilter: "blur(4px)",
            animation: "fadeIn 0.22s cubic-bezier(0.32, 0.72, 0, 1) both",
            willChange: "opacity",
          }} />
          <div onClick={e => e.stopPropagation()} style={modalShellStyle({ vh: appVh, kbOpen: appKbOpen, offsetTop: appOffsetTop, glass, F, dark, width: 380, radius: 22, zIndex: 91 })}>
            <ModalHeader
              t={t} dark={dark} F={F}
              title="Versenden"
              subtitle={<><strong style={{ color: t.text, fontWeight: 700 }}>#{shipChoice.bestellnummer}</strong>{shipChoice.trackingNumber ? <><br/><span style={{ fontSize: 12, color: t.textSub, fontFamily: "ui-monospace, SFMono-Regular, Menlo, monospace" }}>{shipChoice.trackingNumber}</span></> : null}</>}
              onClose={() => setShipChoice(null)}
            />

            {/* Primary CTA — Scanner */}
            <button onClick={() => { setShipChoice(null); setShowScanner(true); }} style={{
              width: "100%", padding: "16px 18px", marginBottom: 10,
              background: "linear-gradient(180deg, #378ADD 0%, #0066D6 100%)",
              border: "none", borderRadius: 14,
              color: "#fff", fontFamily: F, fontWeight: 700, fontSize: 16, letterSpacing: "-0.2px",
              cursor: "pointer",
              display: "flex", alignItems: "center", justifyContent: "center", gap: 10,
              boxShadow: "0 6px 22px rgba(55,138,221,0.4)",
            }}>
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M3 7V5a2 2 0 0 1 2-2h2"/><path d="M17 3h2a2 2 0 0 1 2 2v2"/>
                <path d="M21 17v2a2 2 0 0 1-2 2h-2"/><path d="M7 21H5a2 2 0 0 1-2-2v-2"/>
                <path d="M7 12h10"/>
              </svg>
              Versandlabel scannen
            </button>
            <div style={{ fontSize: 11, color: t.textMuted, textAlign: "center", marginBottom: 18, lineHeight: 1.4 }}>
              Scan-Pfad — Standard. Trackingnummer wird automatisch verifiziert.
            </div>

            {/* Divider */}
            <div style={{ height: 0.5, background: t.divider, marginBottom: 14 }} />

            {/* Manual fallback — secondary, intentionally less prominent. Abbrechen entfällt
                weil der Header schon einen ✕-Close-Button hat (einheitlich mit anderen Modals). */}
            <button onClick={() => { const o = shipChoice; setShipChoice(null); setManualShip(o); }} style={{
              width: "100%", padding: "12px",
              background: "transparent", border: `1px solid ${t.glassBorder}`, borderRadius: 12,
              color: t.textSub, fontFamily: F, fontWeight: 500, fontSize: 13, cursor: "pointer",
              display: "flex", alignItems: "center", justifyContent: "center", gap: 6,
            }}>
              <span>Manuell bestätigen</span>
              <span style={{ fontSize: 10, color: t.textMuted }}>(Persönliche Übergabe etc.)</span>
            </button>
          </div>
        </>
      )}

      {/* ─────────────────────────────────────────────────────────
          BEREIT → VERSENDET — Stufe 2: ManualShipConfirmModal
          Doppelte Bestätigung: Grund-Auswahl + Checkbox + Confirm.
          Erst wenn beide gesetzt sind ist der Confirm-Button aktiv.
          Grund landet in der Notiz für Audit-Trail.
          ───────────────────────────────────────────────────────── */}
      {manualShip && (() => {
        const REASONS = [
          { id: "handover", label: "Persönliche Übergabe" },
          { id: "pickup",   label: "Lokale Abholung" },
          { id: "scan_fail",label: "Scanner funktioniert nicht" },
          { id: "other",    label: "Anderer Grund" },
        ];
        return (
          <ManualShipConfirmModal
            order={manualShip}
            reasons={REASONS}
            t={t} dark={dark} F={F} glass={glass}
            vh={appVh} kbOpen={appKbOpen} offsetTop={appOffsetTop}
            onClose={() => setManualShip(null)}
            onConfirm={(reasonLabel, reasonNote) => {
              const id = manualShip.id;
              const ordnumber = manualShip.bestellnummer;
              const stamp = new Date().toLocaleDateString("de-DE", { day: "2-digit", month: "2-digit", year: "numeric" });
              const tag = `[Manuell als versendet markiert · ${reasonLabel}${reasonNote ? `: ${reasonNote}` : ""} · ${stamp}]`;
              const order = orders.find(x => x.id === id);
              const newNotiz = order?.notiz ? `${order.notiz}\n${tag}` : tag;
              setOrders(o => o.map(x => x.id !== id ? x : { ...x, status: "Versendet", notiz: newNotiz }));
              apiOrderUpdate(id, { status: "Versendet", notiz: newNotiz });
              setManualShip(null);
              setHelperSuccessBanner({ id, bestellnummer: ordnumber });
              haptic(20);
              setTimeout(() => setHelperSuccessBanner(curr => curr?.id === id ? null : curr), 5000);
            }}
          />
        );
      })()}
      {/* ── HELFER: ERFOLGS-BANNER mit Undo ── */}
      {helperSuccessBanner && (
        <div style={{
          position: "fixed",
          top: "calc(env(safe-area-inset-top, 16px) + 16px)",
          left: "50%",
          transform: "translateX(-50%)",
          zIndex: 100,
          ...glass({ borderRadius: 50, border: "1px solid rgba(48,209,88,0.4)" }),
          padding: "10px 8px 10px 18px",
          display: "flex", alignItems: "center", gap: 12,
          animation: "toastIn 0.32s cubic-bezier(0.34,1.4,0.64,1)",
          boxShadow: "0 8px 32px rgba(48,209,88,0.25)",
          background: dark ? "rgba(20,40,28,0.92)" : "rgba(232,250,238,0.96)",
          maxWidth: "calc(100vw - 64px)",
        }}>
          <div style={{
            width: 28, height: 28, borderRadius: "50%", background: "var(--accent-stand)",
            display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0,
          }}>
            <svg width="16" height="14" viewBox="0 0 16 14" fill="none">
              <path d="M2 7.5L6 11.5L14 2.5" stroke="#fff" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round"/>
            </svg>
          </div>
          <div style={{ display: "flex", flexDirection: "column", gap: 1, minWidth: 0 }}>
            <span style={{ fontSize: 15, fontWeight: 700, color: "var(--accent-stand)", fontFamily: F, letterSpacing: "-0.2px" }}>Verschickt!</span>
            <span style={{ fontSize: 12, color: t.textSub, fontWeight: 500, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
              #{helperSuccessBanner.bestellnummer}
            </span>
          </div>
          {/* Undo-Knopf — setzt Status zurück auf Bereit */}
          <button onClick={() => {
            const id = helperSuccessBanner.id;
            setOrders(o => o.map(x => x.id !== id ? x : { ...x, status: "Bereit" }));
            apiOrderUpdate(id, { status: "Bereit" });
            setHelperSuccessBanner(null);
            haptic(15);
          }} style={{
            background: dark ? "rgba(255,255,255,0.1)" : "rgba(0,0,0,0.06)",
            border: "none",
            borderRadius: 50,
            padding: "8px 14px",
            fontFamily: F,
            fontWeight: 600,
            fontSize: 13,
            color: "var(--accent-online)",
            cursor: "pointer",
            flexShrink: 0,
            display: "flex",
            alignItems: "center",
            gap: 4,
          }}>
            <svg width="13" height="13" viewBox="0 0 13 13" fill="none" style={{ marginTop: -1 }}>
              <path d="M2 4.5h6.5a3 3 0 0 1 0 6H6.5" stroke="var(--accent-online)" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round"/>
              <path d="M4.5 2L2 4.5L4.5 7" stroke="var(--accent-online)" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round"/>
            </svg>
            Rückgängig
          </button>
        </div>
      )}
      {/* ── UNRETOURE CONFIRM ── */}
      {confirmUnretoure !== null && (() => {
        const order = orders.find(o => o.id === confirmUnretoure);
        if (!order) return null;
        return (
          <>
            <div onClick={() => setConfirmUnretoure(null)} style={{
              position: "fixed", inset: 0, zIndex: 90,
              background: dark ? "rgba(0,0,0,0.55)" : "rgba(0,0,0,0.34)",
              backdropFilter: "blur(4px)", WebkitBackdropFilter: "blur(4px)",
              animation: "fadeIn 0.22s cubic-bezier(0.32, 0.72, 0, 1) both",
              willChange: "opacity",
            }} />
            <div onClick={e => e.stopPropagation()} style={modalShellStyle({ vh: appVh, kbOpen: appKbOpen, offsetTop: appOffsetTop, glass, F, dark, width: 360, radius: 22, zIndex: 91 })}>
              <ModalHeader
                t={t} dark={dark} F={F}
                title="Retoure zurücksetzen?"
                subtitle={<>#{order.bestellnummer}</>}
                accent={t.accent.blue}
                accentIcon={
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M3 12a9 9 0 1 0 9-9"/><path d="M3 4v5h5"/>
                  </svg>
                }
                onClose={() => setConfirmUnretoure(null)}
              />
              <div style={{ fontSize: 13, color: t.textSub, lineHeight: 1.5, marginBottom: 16, padding: "10px 12px", background: t.accent.orange.weak, borderRadius: 10, border: `1px solid ${t.accent.orange.solid}33` }}>
                Diese Bestellung ist als <strong style={{ color: t.text }}>Retoure</strong> markiert. Zurück auf Versendet setzen macht normalerweise keinen Sinn — nur bei Fehler-Klicks.
              </div>
              <button onClick={() => setConfirmUnretoure(null)} style={{
                width: "100%", padding: "13px", marginBottom: 8,
                background: "rgba(55,138,221,0.92)", border: "none", borderRadius: 12,
                fontFamily: F, fontWeight: 700, fontSize: 15, cursor: "pointer", color: "#fff",
                letterSpacing: "-0.1px", boxShadow: "0 4px 20px rgba(55,138,221,0.3)",
              }}>Abbrechen</button>
              <button onClick={confirmUnretoureNow} style={{
                width: "100%", padding: "11px",
                background: "transparent", border: "none",
                fontFamily: F, fontWeight: 500, fontSize: 13, cursor: "pointer", color: t.accent.red.solid,
              }}>Ja, war ein Fehler</button>
            </div>
          </>
        );
      })()}
    </>
  );
}
